import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { BaseDialog } from '@components/common/BaseDialog';
import { ModalIndices } from '@/common/i-doc/i-doc.registry';
import { getMetricInfo, MetricInfoResponse } from '@services/calculated-metrics.service';
import { CalculationStep } from '@/types/settings.types';
import { dailyNormEditDialog, metricExplanationDialog as t } from '@/styles/tokens';

interface MetricExplanationDialogProps {
  open: boolean;
  onClose: () => void;
  /** Код калькулятора: bmr, wdna, amdr. */
  metric: string | null;
  /** Розрахунок цієї формули для конкретного клієнта (її крок calculation_details); null - показати загальний приклад. */
  clientSteps: CalculationStep[] | null;
}

const HEADING_SX = { fontSize: 16, lineHeight: '19px', fontWeight: 600, color: t.text } as const;
const TEXT_SX = { fontSize: 16, lineHeight: '22px', color: t.text } as const;

// Емодзі з тексту калькулятора (1️⃣, ⚠️, 💧…) - не за макетом; keycap-цифра лишається цифрою.
const stripEmoji = (text: string) => text.replace(/[\p{Extended_Pictographic}\uFE0F\u20E3]/gu, '').replace(/[ \t]{2,}/g, ' ');

// Текст пояснення калькулятора: абзаци через порожній рядок, "- " - пункт списку, "Підпис:" - жирний підпис.
const ExplanationText: React.FC<{ text: string }> = ({ text }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    {stripEmoji(text).split(/\n\s*\n/).map((block, i) => {
      const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
      const items = lines.filter((line) => line.startsWith('- '));
      const rest = lines.filter((line) => !line.startsWith('- '));
      return (
        <Box key={i}>
          {rest.map((line, j) => {
            const label = line.match(/^([^:\d]{1,40}:)(.*)$/);
            return (
              <Typography key={j} sx={TEXT_SX}>
                {label ? <><Box component="span" sx={{ fontWeight: 600 }}>{label[1]}</Box>{label[2]}</> : line}
              </Typography>
            );
          })}
          {items.length > 0 && (
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {items.map((item, j) => (
                <Typography key={j} component="li" sx={TEXT_SX}>{item.slice(2)}</Typography>
              ))}
            </Box>
          )}
        </Box>
      );
    })}
  </Box>
);

// Значення кроку: число (до 0.01), назва (схема харчування) або набір (БЖВ, г).
const formatStepValue = (value: CalculationStep['value']): string =>
  typeof value === 'number'
    ? String(Math.round(value * 100) / 100)
    : typeof value === 'string'
      ? value
      : Object.entries(value as Record<string, number>).map(([key, v]) => `${key} ${Math.round(Number(v) * 10) / 10}`).join(' · ');

// Джерело калькулятора - "Опис: https://..." / "Опис - https://...": посилання на URL, текст - весь рядок.
const SourceLink: React.FC<{ source: string }> = ({ source }) => {
  const url = source.match(/https?:\/\/\S+/)?.[0];
  const sx = { fontSize: 14, lineHeight: '22px', fontWeight: 500, color: t.link, textDecoration: 'underline' } as const;
  return url ? (
    <Typography component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ ...sx, display: 'block' }}>
      {source}
    </Typography>
  ) : (
    <Typography sx={sx}>{source}</Typography>
  );
};

/**
 * Пояснення розрахунку метрики (Figma): опис і джерела - з калькулятора (GET /calculated-metrics/:metric/info),
 * пояснення - розрахунок цієї формули для цього клієнта (її крок у розрахунку денних норм); без нього - загальний
 * приклад калькулятора.
 */
export const MetricExplanationDialog: React.FC<MetricExplanationDialogProps> = ({ open, onClose, metric, clientSteps }) => {
  const { t: tr } = useTranslation(undefined, { keyPrefix: 'clientProfile.dailyNorms.explanation' });
  const [info, setInfo] = useState<MetricInfoResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || !metric) return;
    let cancelled = false;
    setInfo(null);
    setError(false);
    getMetricInfo(metric)
      .then((data) => { if (!cancelled) setInfo(data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [open, metric]);

  const code = metric?.toUpperCase() ?? '';
  const title = info?.caption ? `${info.caption} (${code})` : code;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={title}
      iDoc={ModalIndices.METRIC_EXPLANATION}
      maxWidth={false}
      paperSx={{ width: 580, maxWidth: 'calc(100% - 32px)', m: 2, borderRadius: '16px', boxShadow: dailyNormEditDialog.shadow }}
      headerSx={{
        p: '16px 24px',
        minHeight: 64,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        '& > .MuiBox-root': { width: '100%' },
        '& .MuiIconButton-root': { width: 32, height: 32, p: 0, ml: '14px', color: dailyNormEditDialog.close },
      }}
      titleSx={{ fontSize: 20, lineHeight: '23px', fontWeight: 600, color: dailyNormEditDialog.text }}
      contentSx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, borderColor: t.divider }}
      actionsSx={{ height: 80, px: 3, py: 0 }}
      actions={
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            width: 120,
            height: 40,
            borderRadius: '6px',
            fontSize: 14,
            lineHeight: '20px',
            fontWeight: 500,
            color: dailyNormEditDialog.accent,
            borderColor: dailyNormEditDialog.accent,
            '&:hover': { borderColor: dailyNormEditDialog.accentHover, bgcolor: 'transparent' },
          }}
        >
          {tr('close')}
        </Button>
      }
    >
      {error ? (
        <Alert severity="warning">{tr('loadError')}</Alert>
      ) : !info ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={HEADING_SX}>{tr('description')}</Typography>
            <Typography sx={TEXT_SX}>{info.description ?? '—'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={HEADING_SX}>{tr('explanation')}</Typography>
            {clientSteps?.length ? (
              clientSteps.map((step) => (
                <Box key={step.step} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography sx={TEXT_SX}>
                    <Box component="span" sx={{ fontWeight: 600 }}>{step.step}. {step.name}:</Box> {formatStepValue(step.value)}
                  </Typography>
                  <ExplanationText text={step.formula} />
                </Box>
              ))
            ) : (
              <>
                <Alert severity="info">{tr('example')}</Alert>
                <ExplanationText text={info.explanation ?? ''} />
              </>
            )}
          </Box>
          {(info.sources?.length ?? 0) > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={HEADING_SX}>{tr('sources')}</Typography>
              {info.sources?.map((source) => <SourceLink key={source} source={source} />)}
            </Box>
          )}
        </>
      )}
    </BaseDialog>
  );
};
